import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * Text-to-speech via the Web Speech API.
 * Speaks an array of text chunks sequentially so long articles don't hit the
 * ~15s single-utterance cutoff some browsers impose, and so we can report
 * paragraph-level progress. Exposes play/pause/resume/stop, rate, and voice.
 */
const isSupported = () => typeof window !== 'undefined' && 'speechSynthesis' in window;

const pickVoice = (voices) => {
    const en = voices.filter((v) => /^en(-|_|$)/i.test(v.lang));
    const pool = en.length ? en : voices;
    const preferred = ['Google US English', 'Samantha', 'Microsoft Aria', 'Microsoft Jenny', 'Natural'];
    for (const name of preferred) {
        const hit = pool.find((v) => v.name.includes(name));
        if (hit) return hit;
    }
    return pool.find((v) => v.default) || pool[0] || null;
};

const useSpeech = () => {
    const supported = isSupported();
    const [speaking, setSpeaking] = useState(false);
    const [paused, setPaused] = useState(false);
    const [current, setCurrent] = useState(-1);
    const [rate, setRateState] = useState(1);

    const chunksRef = useRef([]);
    const idxRef = useRef(0);
    const rateRef = useRef(1);
    const voiceRef = useRef(null);
    const keepAliveRef = useRef(null);
    const stoppedRef = useRef(false);

    // Load voices (async on some browsers)
    useEffect(() => {
        if (!supported) return undefined;
        const synth = window.speechSynthesis;
        const setVoice = () => { voiceRef.current = pickVoice(synth.getVoices() || []); };
        setVoice();
        synth.addEventListener('voiceschanged', setVoice);
        return () => synth.removeEventListener('voiceschanged', setVoice);
    }, [supported]);

    const clearKeepAlive = () => {
        if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null; }
    };

    const stop = useCallback(() => {
        if (!supported) return;
        stoppedRef.current = true;
        clearKeepAlive();
        window.speechSynthesis.cancel();
        setSpeaking(false);
        setPaused(false);
        setCurrent(-1);
        idxRef.current = 0;
    }, [supported]);

    const speakFrom = useCallback((startIndex) => {
        if (!supported) return;
        const synth = window.speechSynthesis;
        synth.cancel();
        stoppedRef.current = false;
        idxRef.current = startIndex;

        const speakNext = () => {
            if (stoppedRef.current) return;
            const i = idxRef.current;
            if (i >= chunksRef.current.length) {
                clearKeepAlive();
                setSpeaking(false);
                setPaused(false);
                setCurrent(-1);
                idxRef.current = 0;
                return;
            }
            setCurrent(i);
            const u = new SpeechSynthesisUtterance(chunksRef.current[i]);
            if (voiceRef.current) u.voice = voiceRef.current;
            u.rate = rateRef.current;
            u.onend = () => {
                if (stoppedRef.current) return;
                idxRef.current += 1;
                speakNext();
            };
            u.onerror = () => {
                if (stoppedRef.current) return;
                idxRef.current += 1;
                speakNext();
            };
            synth.speak(u);
        };

        clearKeepAlive();
        // Some Chromium builds silently pause long playback; nudge it to resume.
        keepAliveRef.current = setInterval(() => {
            if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
                window.speechSynthesis.resume();
            }
        }, 10000);

        setSpeaking(true);
        setPaused(false);
        speakNext();
    }, [supported]);

    const play = useCallback((chunks, startIndex = 0) => {
        if (!supported || !chunks || !chunks.length) return;
        chunksRef.current = chunks;
        speakFrom(startIndex);
    }, [supported, speakFrom]);

    const pause = useCallback(() => {
        if (!supported) return;
        window.speechSynthesis.pause();
        setPaused(true);
    }, [supported]);

    const resume = useCallback(() => {
        if (!supported) return;
        window.speechSynthesis.resume();
        setPaused(false);
    }, [supported]);

    const setRate = useCallback((r) => {
        rateRef.current = r;
        setRateState(r);
        // apply immediately by restarting from the current chunk if we're speaking
        if (window.speechSynthesis && window.speechSynthesis.speaking) {
            speakFrom(idxRef.current);
        }
    }, [speakFrom]);

    // Cleanup on unmount
    useEffect(() => () => { if (supported) { stoppedRef.current = true; clearKeepAlive(); window.speechSynthesis.cancel(); } }, [supported]);

    return {supported, speaking, paused, current, rate, play, pause, resume, stop, setRate};
};

export default useSpeech;
